import { ExternalAgentIdentifier, YapMoney } from '@/types/general';
import { PhoneNumber, PostalAddress } from '@/types/address';

export enum NaturalPersonURI {
  Base = '/natural-persons',
  BaseV2 = '/v2/natural-persons',
  Me = '/v2/natural-person',

  CashAccounts = '/cash-accounts',
  Cards = '/cards',
  Devices = '/devices',
  Domicile = '/domicile',
  DomicileChange = '/domicile-change',
  EBillSubscriptions = '/ebill-subscriptions',
  ExportTickets = '/export-tickets',
  Invoices = '/invoices',
  MyPicture = '/profile/my-picture',
  PartnershipsEngagements = '/partnerships/engagements',
  PaymentOrders = '/payment-orders',
  PendingPaymentOrders = '/pending-payment-orders',
  PhoneNumber = '/phone-number',
  Profile = '/profile',
  ReferralCode = '/referral-code',
  RelatedBillers = '/related-billers',
  VirtualIdentities = '/virtual-identities'
}

export enum NaturalPersonStatus {
  New = 'new',
  Registered = 'registered',
  Identified = 'identified',
  Screened = 'screened',
  Cancelled = 'cancelled',
  Passive = 'passive',
  Rejected = 'rejected',
  Bankable = 'bankable',
  Retired = 'retired'
}

export enum NaturalPersonGender {
  Male = 'male',
  Female = 'female',
  Unspecific = 'unspecific',
  Unknown = 'unknown',
  Synthetic = 'synthetic'
}

export interface NaturalPerson {
  uid: string;
  base: {
    names?: string[];
    givenNames?: string[];
    gender?: NaturalPersonGender;
    isPassported?: boolean;
    isRegistered?: boolean;
    isDeclared?: boolean;
    isQualified?: boolean;
    identificationOnly?: boolean;
    isEligibleForAccount?: boolean;
    fullName?: string;
  };
  detailed: {
    honorific?: string;
    title?: string;
    nationalities?: string[];
    dateOfBirth?: string;
    placeOfOrigin?: string;
    placeOfBirth?: string;
  };
  sensitive?: {
    signatureFile?: string;
    photoFileName?: string;
    identificationContextUid?: string;
    identificationMode?: 'identification' | 'identity' | 'auth';
  };
  contactDetails: {
    emailAddress: string;
    phoneNumber?: PhoneNumber;
    domicileAddress?: PostalAddress;
  };
  status: NaturalPersonStatus;
  relationshipCreationDate: string;
}

export interface NaturalPersonProfile {
  communication: {
    languageCode?: string;
    locale?: string;
    nickName?: string;
    noAdvertisements?: boolean;
    noNewsletters?: boolean;
    myPicture?: string;
  };
  occupation: {
    occupationType?: string;
    occupation?: string;
    industrySector?: string;
    employer?: string;
    originOfFunds?: string;
  };
  salary: {
    IndicativeSalary?: YapMoney;
    verifiedSalary?: YapMoney;
    salaryLowerRange?: YapMoney;
    salaryUpperRange?: YapMoney;
  };
  investor: {
    riskTolerance?: 'very-low' | 'low' | 'medium' | 'high' | 'very-high';
    lossTolerance?: 'very-low' | 'low' | 'medium' | 'high' | 'very-high';
    experiencedWithFunds?: boolean;
    investedInFunds?: boolean;
  };
  pension: {
    hasFirstPillar?: boolean;
    insuredFirstPillarSalary?: YapMoney;
    hasSecondPillar?: boolean;
    insuredSecondPillarSalary?: YapMoney;
    thirdPillarWithdrawalMade?: boolean;
    thirdPillarInvestmentHorizon?: 'short-term' | 'long-term';
    swissSocialSecNr13?: string;
  };
  personalStatus: {
    maritalStatus?: string;
  };
  relationship: {
    expectedTransactionVolume?: YapMoney;
    mainPurpose?: string;
  };
  education: {
    highestEducation?: string;
  };
  features: {
    hasAvatarPicture: boolean;
    hasProfilePicture: boolean;
    discoverableByOtherYapsters: boolean;
  };
  tax: {
    multiCountryTaxLiable?: boolean;
    domicileTaxInformation?: DomicileTaxInformation[];
  };
  identity: {
    hasMemorablePhotos: boolean;
    hasRecoveryCode: boolean;
    externalIdentifiers?: ExternalAgentIdentifier[];
  };
  affirmation?: {
    affirmed?: boolean;
    lastAffirmationDate?: string;
  };
  risk?: {
    clientID: number;
    profilingLevel?: string;
    assignedRiskCategory?: string;
    assignmentMode?: string;
    assignedRiskRecordDate?: string;
    riskAssessmentCertificateUID?: string;
    pepStdAssessmentCode?: string;
    pepStdAssessment?: string;
    pepStdAssessmentDate?: string;
    kycCheckResult?: string;
    kycCheckTime?: string;
    kycVerificationRisk?: string;
    derivedRiskRecordDate?: string;
    derivedRiskCategory?: string;
    profilingLevelData?: {
      enhancedProfiling: boolean;
      enhancedProfilingStartTime?: string;
      investigationProfiling: boolean;
      investigationProfilingStartTime?: string;
    };
    categoryId?: string;
  };
}

interface DomicileTaxInformation {
  countryCode: string;
  mainDomicile: boolean;
  tinNumber?: string;
  noTinReason?: string;
  noTinReasonText?: string;
}

export interface NaturalPersonProfileCheck {
  missingAttributes: string[];
}

export interface NaturalPersonPhoneNumber {
  agentPhoneNumber: PhoneNumber;
  agentName: {
    names?: string[];
    givenNames?: string[];
  };
}

export interface NaturalPersonEmail {
  uid: string;
  createdAt: string;
  usage: string;
  emailAddress: string;
  verified: boolean;
  status: string;
  unconfirmedEmailAddress?: string;
}

export interface NaturalPersonDomicileAddress {
  agentAddress: PostalAddress;
  agentName: {
    names?: string[];
    givenNames?: string[];
  };
  planned?: {
    agentAddress: PostalAddress;
    validFrom: string;
  };
}
